// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../../helpers/constants/global-props';

import { ColumnWrapper, ColumnSizes, OffsetSizes } from '../styles';

export interface ColumnProps {
  /** Column inner content */
  children?: ReactNode;
  /** Width of the column from `Mobile` devices **up** */
  mobile: ColumnSizes;
  /** Width of the column from `Tablet portrait` devices **up** */
  tabletPortrait?: ColumnSizes;
  /** Width of the column from `Tablet landscape` devices **up** */
  tabletLandscape?: ColumnSizes;
  /** Width of the column from `Desktop` devices **up** */
  desktop?: ColumnSizes;
  /** Width of the column from `Desktop large` devices **up** */
  desktopLarge?: ColumnSizes;
  /** Offset of the column, from the left, from `Mobile` devices **up** */
  offsetMobile?: OffsetSizes;
  /** Offset of the column, from the left, on `Mobile` devices **only** */
  offsetMobileOnly?: OffsetSizes;
  /** Offset of the column, from the left, from `Tablet portrait` devices **up** */
  offsetTabletPortrait?: OffsetSizes;
  /** Offset of the column, from the left, on `Tablet portrait` devices **only** */
  offsetTabletPortraitOnly?: OffsetSizes;
  /** Offset of the column, from the left, from `Tablet landscape` devices **up** */
  offsetTabletLandscape?: OffsetSizes;
  /** Offset of the column, from the left, on `Tablet landscape` devices **only** */
  offsetTabletLandscapeOnly?: OffsetSizes;
  /** Offset of the column, from the left, from `Desktop` devices **up** */
  offsetDesktop?: OffsetSizes;
  /** Offset of the column, from the left, on `Desktop` devices **only** */
  offsetDesktopOnly?: OffsetSizes;
  /** Offset of the column, from the left, from `Desktop large` devices **up** */
  offsetDesktopLarge?: OffsetSizes;
  /** Offset of the column, from the left, on `Desktop large` devices **only** */
  offsetDesktopLargeOnly?: OffsetSizes;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Divides rows horizontally up to 12 parts
 * @categories layout
 * @group Grid
 */
export function Column(props: ColumnProps) {
  const {
    mobile,
    tabletPortrait,
    tabletLandscape,
    desktop,
    desktopLarge,
    offsetMobile,
    offsetTabletPortrait,
    offsetTabletLandscape,
    offsetDesktop,
    offsetDesktopLarge,
    offsetMobileOnly,
    offsetTabletPortraitOnly,
    offsetTabletLandscapeOnly,
    offsetDesktopOnly,
    offsetDesktopLargeOnly,
  } = props;

  const globalProps = getGlobalProps({});

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        columnMobileSize: mobile,
        columnTabletPortraitSize: tabletPortrait,
        columnTabletLandscapeSize: tabletLandscape,
        columnDesktopSize: desktop,
        columnDesktopLargeSize: desktopLarge,
        columnOffsetMobile: offsetMobile,
        columnOffsetTabletPortrait: offsetTabletPortrait,
        columnOffsetTabletLandscape: offsetTabletLandscape,
        columnOffsetDesktop: offsetDesktop,
        columnOffsetDesktopLarge: offsetDesktopLarge,
        columnOffsetMobileOnly: offsetMobileOnly,
        columnOffsetTabletPortraitOnly: offsetTabletPortraitOnly,
        columnOffsetTabletLandscapeOnly: offsetTabletLandscapeOnly,
        columnOffsetDesktopOnly: offsetDesktopOnly,
        columnOffsetDesktopLargeOnly: offsetDesktopLargeOnly,
      })}
    >
      <ColumnWrapper data-testid={props['data-testid']} {...globalProps}>
        {props.children}
      </ColumnWrapper>
    </ThemeProvider>
  );
}

Column.defaultProps = {
  children: undefined,

  tabletPortrait: undefined,
  tabletLandscape: undefined,
  desktop: undefined,
  desktopLarge: undefined,

  offsetMobile: undefined,
  offsetTabletPortrait: undefined,
  offsetTabletLandscape: undefined,
  offsetDesktop: undefined,
  offsetDesktopLarge: undefined,

  offsetMobileOnly: undefined,
  offsetTabletPortraitOnly: undefined,
  offsetTabletLandscapeOnly: undefined,
  offsetDesktopOnly: undefined,
  offsetDesktopLargeOnly: undefined,

  'data-testid': undefined,
};

Column.displayName = 'Column';

export default Column;
